import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Alert from '@/components/base/Alert';
import StatusBadge from '@/components/base/StatusBadge';
import { adminApi } from '@/lib/adminApi';
import type { AdminClient, AdminTransaction, AdminTransfer, AdminCredentials } from '@/lib/adminApi';
import { formatCardBrand, formatDate, formatMoney } from '@/lib/format';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, Modal, TableCell, TableHeaderCell } from './ui';

interface DetailBundle {
  client: AdminClient;
  transactions: AdminTransaction[];
  transfers: AdminTransfer[];
}

export default function ClientsTab({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailBundle | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [newCode, setNewCode] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustLabel, setAdjustLabel] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [clientCredentials, setClientCredentials] = useState<AdminCredentials | null>(null);
  const [credStatus, setCredStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await adminApi.clientsList(token);
      if (!res.ok) {
        setFailed(true);
        return;
      }
      setClients((res.clients ?? []) as AdminClient[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDetail = useCallback(
    async (clientId: string) => {
      setDetailLoading(true);
      try {
        const res = await adminApi.clientDetail(token, clientId);
        if (res.ok) {
          setDetail({
            client: res.client as AdminClient,
            transactions: (res.transactions ?? []) as AdminTransaction[],
            transfers: (res.transfers ?? []) as AdminTransfer[],
          });
        }
      } finally {
        setDetailLoading(false);
      }
    },
    [token],
  );

  const openDetail = (clientId: string) => {
    setDetailId(clientId);
    setDetail(null);
    setNote('');
    setNewCode('');
    setAdjustAmount('');
    setAdjustLabel('');
    setConfirmDelete(false);
    setClientCredentials(null);
    setCredStatus('');
    loadDetail(clientId);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return clients;
    return clients.filter((client) =>
      [client.fullName, client.email ?? '', client.phone ?? '', client.bankLogin ?? '']
        .join(' ')
        .toLowerCase()
        .includes(value),
    );
  }, [clients, query]);

  const refreshDetail = async (clientId: string) => {
    await loadDetail(clientId);
    await load();
  };

  const adjustBalance = async () => {
    if (!detailId || busy) return;
    const amount = Number(adjustAmount);
    if (!Number.isFinite(amount) || amount === 0) {
      setNote(t('admin.clients.detail.invalidAmount'));
      return;
    }
    setBusy(true);
    const res = await adminApi.clientAdjustBalance(token, detailId, amount, adjustLabel.trim());
    setBusy(false);
    if (!res.ok) {
      setNote(t('admin.clients.detail.balanceError'));
      return;
    }
    setAdjustAmount('');
    setAdjustLabel('');
    setNote(t('admin.clients.detail.updated'));
    refreshDetail(detailId);
  };

  const regenerate = async () => {
    if (!detailId || busy) return;
    setBusy(true);
    const res = await adminApi.clientRegenerateCode(token, detailId);
    setBusy(false);
    if (res.ok && typeof res.accessCode === 'string') {
      setNewCode(res.accessCode);
      setNote(t('admin.clients.detail.updated'));
      refreshDetail(detailId);
    }
  };

  const toggleStatus = async () => {
    if (!detailId || !detail || busy) return;
    setBusy(true);
    const nextStatus = detail.client.status === 'active' ? 'disabled' : 'active';
    const res = await adminApi.clientUpdate(token, detailId, { status: nextStatus });
    setBusy(false);
    if (res.ok) {
      setNote(t('admin.clients.detail.updated'));
      refreshDetail(detailId);
    }
  };

  const removeClient = async () => {
    if (!detailId || busy) return;
    setBusy(true);
    const res = await adminApi.dataDelete(token, 'client', detailId);
    setBusy(false);
    setConfirmDelete(false);
    if (res.ok) {
      closeDetail();
      load();
    } else {
      setNote(t('errors.server'));
    }
  };

  const sendCredentials = async () => {
    if (!detailId || busy || !detail) return;
    const target = detail.client.email ?? '';
    if (!target) {
      setNote(t('admin.clients.detail.noEmail'));
      return;
    }
    setBusy(true);
    const res = await adminApi.credentialsSend(token, detailId, target);
    setBusy(false);
    if (!res.ok) {
      setNote(t('errors.server'));
      return;
    }
    setClientCredentials((res.credentials ?? null) as AdminCredentials | null);
    setCredStatus(String(res.emailStatus ?? ''));
    setNote(t('admin.clients.detail.updated'));
  };

  return (
    <div className="flex flex-col gap-5">
      <AdminCard
        title={t('admin.clients.title')}
        subtitle={t('admin.clients.subtitle')}
        actions={
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center justify-center w-4 h-4 text-foreground-400">
              <i className="ri-search-line text-sm" aria-hidden="true" />
            </span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('admin.clients.search')}
              className="h-9 w-full sm:w-56 box-border rounded-full border border-background-300 bg-background-50 pl-9 pr-3 text-[12.5px] text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition-colors"
            />
          </div>
        }
        padded={false}
      >
        {loading ? (
          <LoadingBlock />
        ) : failed ? (
          <ErrorBlock onRetry={load} />
        ) : filtered.length === 0 ? (
          <EmptyBlock label={t('admin.clients.empty')} icon="ri-group-line" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[940px]">
              <thead className="bg-background-100">
                <tr>
                  <TableHeaderCell>{t('admin.clients.name')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.clients.card')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.clients.bankLogin')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.clients.currency')}</TableHeaderCell>
                  <TableHeaderCell align="right">{t('admin.clients.balance')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.clients.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('admin.clients.actions')}</TableHeaderCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {filtered.map((client) => (
                  <tr key={client.id} className="hover:bg-background-100/60 transition-colors">
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => openDetail(client.id)}
                        className="text-left cursor-pointer group"
                      >
                        <span className="block text-[13px] font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                          {client.fullName}
                        </span>
                        <span className="block text-[11.5px] text-foreground-500">{client.email ?? '—'}</span>
                      </button>
                    </TableCell>
                    <TableCell>
                      {client.cardBrand || client.cardLast4 ? (
                        <span className="inline-flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-2 py-1">
                          <span className="flex items-center justify-center w-4 h-4 shrink-0 text-foreground-500">
                            <i className="ri-bank-card-line text-sm" aria-hidden="true" />
                          </span>
                          <span className="flex flex-col leading-tight">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground-900 font-label whitespace-nowrap">
                              {formatCardBrand(client.cardBrand)}
                            </span>
                            <span className="text-[11px] text-foreground-500 font-label tracking-[0.08em] whitespace-nowrap tabular">
                              ···· {client.cardLast4 ?? '····'}
                            </span>
                          </span>
                        </span>
                      ) : (
                        <span className="text-[12px] text-foreground-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-label text-[12px] text-foreground-600">{client.bankLogin ?? '—'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md bg-background-100 border border-background-200 text-[11px] font-semibold text-foreground-700 font-label">
                        {client.currency}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span className="font-semibold text-foreground-950">
                        {formatMoney(client.balance, client.currency, i18n.language)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label whitespace-nowrap ${
                          client.status === 'active'
                            ? 'bg-primary-50 text-primary-800 border-primary-200'
                            : 'bg-background-200 text-foreground-600 border-background-300'
                        }`}
                      >
                        <span className="flex items-center justify-center w-3 h-3">
                          <i
                            className={client.status === 'active' ? 'ri-check-line text-[11px]' : 'ri-pause-line text-[11px]'}
                            aria-hidden="true"
                          />
                        </span>
                        {client.status === 'active' ? t('admin.clients.active') : t('admin.clients.disabled')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => openDetail(client.id)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <span className="flex items-center justify-center w-3.5 h-3.5">
                          <i className="ri-eye-line text-[13px]" aria-hidden="true" />
                        </span>
                        {t('admin.clients.manage')}
                      </button>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {/* Detail modal */}
      <Modal
        open={Boolean(detailId)}
        title={detail?.client.fullName ?? t('admin.clients.detail.title')}
        subtitle={detail ? `${detail.client.currency} · ${t('admin.clients.detail.since')} ${formatDate(detail.client.createdAt, i18n.language)}` : ''}
        onClose={closeDetail}
        width="max-w-3xl"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeDetail}>
              {t('admin.common.close')}
            </Button>
            {detail ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  loading={busy}
                  onClick={sendCredentials}
                  icon={<i className="ri-mail-send-line" aria-hidden="true" />}
                >
                  {t('admin.clients.detail.sendCredentials')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  loading={busy}
                  onClick={toggleStatus}
                  icon={<i className={detail.client.status === 'active' ? 'ri-pause-line' : 'ri-play-line'} aria-hidden="true" />}
                >
                  {detail.client.status === 'active' ? t('admin.clients.detail.disable') : t('admin.clients.detail.enable')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  className="!text-accent-700 hover:!bg-accent-50"
                  icon={<i className="ri-delete-bin-line" aria-hidden="true" />}
                >
                  {t('admin.common.delete')}
                </Button>
              </>
            ) : null}
          </>
        }
      >
        {detailLoading || !detail ? (
          <LoadingBlock />
        ) : (
          <div className="flex flex-col gap-4">
            {note ? <Alert tone="info">{note}</Alert> : null}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-background-200 bg-background-100 p-3">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500 font-label">
                  {t('admin.clients.balance')}
                </p>
                <p className="mt-1 text-[17px] font-semibold text-foreground-950 tabular">
                  {formatMoney(detail.client.balance, detail.client.currency, i18n.language)}
                </p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-100 p-3">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500 font-label">
                  {t('admin.clients.bankLogin')}
                </p>
                <p className="mt-1 text-[13px] font-semibold text-foreground-950 font-label truncate">
                  {detail.client.bankLogin ?? '—'}
                </p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-100 p-3">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-foreground-500 font-label">
                  {t('admin.clients.detail.card')}
                </p>
                <p className="mt-1 text-[13px] font-semibold text-foreground-950 font-label">
                  {formatCardBrand(detail.client.cardBrand)} ···· {detail.client.cardLast4 ?? '····'}
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-background-200 p-3.5">
              <h3 className="text-[12.5px] font-semibold text-foreground-900 font-label mb-2.5">
                {t('admin.clients.detail.adjust')}
              </h3>
              <div className="flex flex-col sm:flex-row gap-2.5">
                <div className="w-full sm:w-36 min-w-0">
                  <Input
                    name="adjustAmount"
                    type="number"
                    step="0.01"
                    value={adjustAmount}
                    onChange={(event) => setAdjustAmount(event.target.value)}
                    placeholder="0.00"
                    aria-label={t('admin.clients.detail.adjustAmount')}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Input
                    name="adjustLabel"
                    value={adjustLabel}
                    onChange={(event) => setAdjustLabel(event.target.value)}
                    placeholder={t('admin.clients.detail.adjustLabel')}
                    aria-label={t('admin.clients.detail.adjustLabel')}
                  />
                </div>
                <Button size="md" loading={busy} onClick={adjustBalance} className="shrink-0">
                  {t('admin.clients.detail.adjustSubmit')}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" loading={busy} onClick={regenerate} icon={<i className="ri-refresh-line" aria-hidden="true" />}>
                {t('admin.clients.detail.regen')}
              </Button>
            </div>

            {newCode ? (
              <Alert tone="success" title={t('admin.clients.detail.newCode')}>
                <span className="font-label font-semibold tracking-wide">{newCode}</span>
              </Alert>
            ) : null}

            {clientCredentials ? (
              <div className="rounded-xl border border-primary-200 bg-primary-50 p-3.5">
                <p className="text-[12px] font-semibold text-primary-900 font-label">
                  {t('admin.clients.detail.credentialsTitle')}
                </p>
                <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {[
                    { label: t('admin.clients.create.accessCode'), value: clientCredentials.accessCode },
                    { label: t('admin.clients.create.bankLogin'), value: clientCredentials.bankLogin },
                    { label: t('admin.clients.create.bankPassword'), value: clientCredentials.bankPassword },
                  ].map((item) => (
                    <div key={item.label} className="min-w-0">
                      <dt className="text-[10.5px] uppercase tracking-[0.08em] text-foreground-500 font-label">
                        {item.label}
                      </dt>
                      <dd className="mt-0.5 font-label text-[12.5px] font-semibold text-foreground-950 break-all">
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-2 text-[11.5px] text-primary-900/80">
                  {credStatus === 'sent'
                    ? t('admin.cred.mailSent', { email: clientCredentials.email })
                    : t('admin.cred.mailNotSent')}
                </p>
              </div>
            ) : null}

            <div>
              <h3 className="text-[12.5px] font-semibold text-foreground-900 font-label mb-2">
                {t('admin.clients.detail.transfers')}
              </h3>
              {detail.transfers.length === 0 ? (
                <EmptyBlock label={t('admin.transfers.empty')} icon="ri-exchange-dollar-line" />
              ) : (
                <ul className="divide-y divide-background-200 rounded-xl border border-background-200 overflow-hidden">
                  {detail.transfers.map((row) => (
                    <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5">
                      <div className="min-w-0">
                        <p className="text-[12px] font-semibold text-foreground-950 font-label">{row.reference}</p>
                        <p className="text-[11px] text-foreground-500">
                          {row.bankName} · {formatDate(row.createdAt, i18n.language, true)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <span className="text-[12px] font-semibold tabular text-foreground-800">
                          {formatMoney(row.amount, row.currency, i18n.language)}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold font-label whitespace-nowrap ${
                            row.linkActive
                              ? 'bg-primary-50 text-primary-800 border-primary-200'
                              : 'bg-background-200 text-foreground-600 border-background-300'
                          }`}
                        >
                          <i
                            className={row.linkActive ? 'ri-link text-[11px]' : 'ri-link-unlink text-[11px]'}
                            aria-hidden="true"
                          />
                          {row.linkActive ? t('admin.transfers.linkActive') : t('admin.transfers.linkDisabled')}
                        </span>
                        <StatusBadge status={row.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="text-[12.5px] font-semibold text-foreground-900 font-label mb-2">
                {t('admin.clients.detail.transactions')}
              </h3>
              {detail.transactions.length === 0 ? (
                <EmptyBlock label={t('admin.clients.detail.noTx')} icon="ri-exchange-line" />
              ) : (
                <div className="rounded-xl border border-background-200 overflow-x-auto">
                  <table className="w-full min-w-[460px]">
                    <thead className="bg-background-100">
                      <tr>
                        <TableHeaderCell>{t('admin.logs.date')}</TableHeaderCell>
                        <TableHeaderCell>{t('dash.table.label')}</TableHeaderCell>
                        <TableHeaderCell align="right">{t('dash.table.amount')}</TableHeaderCell>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200">
                      {detail.transactions.map((row) => (
                        <tr key={row.id}>
                          <TableCell>
                            <span className="text-foreground-500">{formatDate(row.createdAt, i18n.language)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-[12.5px]">{row.label}</span>
                          </TableCell>
                          <TableCell align="right">
                            <span
                              className={`font-semibold ${
                                row.direction === 'credit' ? 'text-primary-700' : 'text-foreground-900'
                              }`}
                            >
                              {row.direction === 'credit' ? '+' : '−'}
                              {formatMoney(row.amount, row.currency, i18n.language)}
                            </span>
                          </TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDelete}
        title={t('admin.clients.detail.deleteTitle')}
        subtitle={detail?.client.fullName}
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button
              variant="dark"
              size="sm"
              loading={busy}
              onClick={removeClient}
              icon={<i className="ri-delete-bin-line" aria-hidden="true" />}
            >
              {t('admin.clients.detail.deleteConfirm')}
            </Button>
          </>
        }
      >
        <Alert tone="error">{t('admin.clients.detail.deleteText')}</Alert>
        {detail ? (
          <div className="mt-3 rounded-xl border border-background-200 bg-background-100 px-4 py-3">
            <p className="text-[12.5px] font-semibold text-foreground-950">{detail.client.fullName}</p>
            <p className="mt-0.5 text-[11.5px] text-foreground-500 break-all">
              {detail.client.email ?? '—'} · {detail.client.bankLogin ?? '—'}
            </p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}